import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Search } from 'lucide-react'
import { getFlatNavItems, isNavActive } from '../nav/navConfig'
import { T } from '../pages/shared'

type Props = { open: boolean; onClose: () => void }

export default function CommandPalette({ open, onClose }: Props) {
  const nav = useNavigate()
  const { pathname } = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)

  const items = useMemo(() => {
    const flat = getFlatNavItems()
    const s = q.trim().toLowerCase()
    if (!s) return flat
    return flat.filter(
      i =>
        i.label.toLowerCase().includes(s) ||
        i.section.toLowerCase().includes(s) ||
        i.to.toLowerCase().includes(s),
    )
  }, [q])

  useEffect(() => {
    if (!open) return
    setQ('')
    setHi(0)
    const t = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(t)
  }, [open])

  useEffect(() => {
    setHi(0)
  }, [q])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHi(h => Math.min(h + 1, Math.max(0, items.length - 1)))
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHi(h => Math.max(h - 1, 0))
      }
      if (e.key === 'Enter' && items[hi]) {
        e.preventDefault()
        nav(items[hi].to)
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, items, hi, nav, onClose])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Go to page"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '12vh 16px 16px',
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
      onMouseDown={e => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        style={{
          width: 'min(520px, 100%)',
          borderRadius: 'var(--r-lg)',
          border: '1px solid var(--glass-card-border)',
          background: 'var(--glass-modal-bg)',
          backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
          WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
          boxShadow: 'var(--glass-card-outer), var(--glass-card-inset)',
          overflow: 'hidden',
        }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            borderBottom: `1px solid ${T.border2}`,
          }}
        >
          <Search size={18} color={T.textMuted} aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search pages…"
            aria-label="Search pages"
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              color: T.textPrimary,
              fontSize: 15,
              fontFamily: T.fontBody,
              outline: 'none',
            }}
          />
          <kbd
            style={{
              fontSize: 10,
              fontFamily: T.fontMono,
              color: T.textMuted,
              padding: '3px 6px',
              borderRadius: 4,
              border: `1px solid ${T.border2}`,
              background: T.surface2,
            }}
          >
            esc
          </kbd>
        </div>
        <ul
          role="listbox"
          style={{
            listStyle: 'none',
            maxHeight: 'min(52vh, 420px)',
            overflowY: 'auto',
            margin: 0,
            padding: 6,
          }}
        >
          {items.length === 0 ? (
            <li style={{ padding: '20px 12px', color: T.textMuted, fontSize: 13, textAlign: 'center' }}>
              No matches
            </li>
          ) : (
            items.map((item, i) => {
              const active = i === hi
              const routeHere = isNavActive(pathname, item.to)
              return (
                <li key={item.to + item.section}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    aria-current={routeHere ? 'page' : undefined}
                    onMouseEnter={() => setHi(i)}
                    onClick={() => {
                      nav(item.to)
                      onClose()
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 12px',
                      borderRadius: 'var(--r-sm)',
                      border: routeHere ? `1px solid color-mix(in srgb, ${T.olivine} 45%, transparent)` : '1px solid transparent',
                      cursor: 'pointer',
                      fontFamily: T.fontBody,
                      fontSize: 14,
                      background: active ? T.surface3 : routeHere ? `color-mix(in srgb, ${T.olivine} 10%, transparent)` : 'transparent',
                      color: T.textPrimary,
                      transition: 'background 0.1s, border-color 0.1s',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>{item.label}</span>
                      {routeHere && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            fontFamily: T.fontDisplay,
                            letterSpacing: 0.6,
                            textTransform: 'uppercase',
                            color: T.olivine,
                            flexShrink: 0,
                            padding: '2px 6px',
                            borderRadius: 4,
                            border: `1px solid color-mix(in srgb, ${T.olivine} 35%, transparent)`,
                            background: `color-mix(in srgb, ${T.olivine} 12%, transparent)`,
                          }}
                        >
                          Here
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{item.section}</span>
                  </button>
                </li>
              )
            })
          )}
        </ul>
        <div
          style={{
            padding: '8px 14px',
            borderTop: `1px solid ${T.border2}`,
            fontSize: 11,
            color: T.textMuted,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <span>↑↓ navigate · ↵ open</span>
          <span>⌘K / Ctrl+K</span>
        </div>
      </div>
    </div>
  )
}
